// ---------------------------------------------------------------------------
// Deterministic lab-table parser (single source).
//
// Imported by the browser (src/lib/evidenceParser.ts re-exports it) and by
// edge functions, so both read uploaded CSV/TSV/text lab files identically.
// No AI involved: values produced here are deterministic comparator inputs.
// Dependency-free on purpose — keep it that way.
// ---------------------------------------------------------------------------

export type MeasurementSource = "file_parsed" | "ai_extracted_pdf";

export interface EvidenceMeasurement {
  name: string;
  cas: string;
  /** Value in `unit` (mg/kg after normalization when convertible). */
  value: number;
  unit: string;
  non_detect: boolean;
  source: MeasurementSource;
  file_id: string;
  file_name: string;
}

const CAS_RE = /^\d{2,7}-\d{2}-\d$/;
const NUM_RE = /^[<>≤]?\s*-?\d[\d,]*(\.\d+)?(e-?\d+)?$/i;
const UNIT_RE = /^(mg\/kg|ug\/kg|µg\/kg|μg\/kg|ppm|ppb|mg\/l|ug\/l|%|pct)$/i;

const NAME_HDR = /analyte|parameter|compound|constituent|chemical|^name$|substance/i;
const RESULT_HDR = /result|value|conc/i;
const CAS_HDR = /cas/i;
const UNIT_HDR = /unit/i;
const QUAL_HDR = /qual|flag/i;
const RL_HDR = /(^|\b)(rl|mdl|pql|lod|loq)(\b|$)|reporting/i;

function splitLine(line: string, delim: string): string[] {
  if (delim === "ws") return line.trim().split(/\s{2,}/).map((c) => c.trim());
  const out: string[] = [];
  let cur = "";
  let quoted = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (quoted && line[i + 1] === '"') {
        cur += '"';
        i++;
      } else quoted = !quoted;
    } else if (ch === delim && !quoted) {
      out.push(cur.trim());
      cur = "";
    } else cur += ch;
  }
  out.push(cur.trim());
  return out;
}

function pickDelimiter(lines: string[]): string {
  const sample = lines.slice(0, 10).join("\n");
  if (sample.includes("\t")) return "\t";
  const semi = (sample.match(/;/g) ?? []).length;
  const comma = (sample.match(/,/g) ?? []).length;
  if (semi > comma) return ";";
  if (comma > 0) return ",";
  return "ws";
}

function parseNumber(raw: string): { value: number | null; lessThan: boolean } {
  const s = raw.replace(/\s+/g, " ").trim();
  const lessThan = /^[<≤]/.test(s);
  const m = s.replace(/,/g, "").match(/-?\d+(\.\d+)?(e-?\d+)?/i);
  if (!m) return { value: null, lessThan };
  const v = Number(m[0]);
  return { value: Number.isFinite(v) ? v : null, lessThan };
}

/** Normalizes to mg/kg where the unit is a soil mass concentration. */
export function normalizeUnit(value: number, unit: string): { value: number; unit: string } {
  const u = unit.trim().toLowerCase().replace("μ", "u").replace("µ", "u");
  if (u === "" || u === "mg/kg" || u === "ppm") return { value, unit: "mg/kg" };
  if (u === "ug/kg" || u === "ppb") return { value: value / 1000, unit: "mg/kg" };
  return { value, unit: unit.trim() };
}

const isMarker = (l: string) => /^(#|\/\/|-{3,}|={3,})/.test(l) || /^\[?(BEGIN|END)\b/i.test(l);

/**
 * Parse a lab results table. Handles header rows (analyte/CAS/result/unit/
 * qualifier/RL columns), headerless "name, value, unit" rows, ND / "<RL" /
 * "U"-qualified non-detects, and ug/kg → mg/kg conversion.
 */
export function parseEvidenceTable(
  text: string,
  file: { id: string; name: string },
): EvidenceMeasurement[] {
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0 && !isMarker(l));
  if (lines.length === 0) return [];
  const delim = pickDelimiter(lines);
  const rows = lines.map((l) => splitLine(l, delim));

  let header: { name: number; result: number; cas: number; unit: number; qual: number; rl: number } | null = null;
  let start = 0;
  for (let i = 0; i < Math.min(rows.length, 15); i++) {
    const r = rows[i];
    const name = r.findIndex((c) => NAME_HDR.test(c));
    const result = r.findIndex((c, idx) => idx !== name && RESULT_HDR.test(c) && !UNIT_HDR.test(c));
    if (name >= 0 && result >= 0) {
      header = {
        name,
        result,
        cas: r.findIndex((c) => CAS_HDR.test(c)),
        unit: r.findIndex((c) => UNIT_HDR.test(c)),
        qual: r.findIndex((c) => QUAL_HDR.test(c)),
        rl: r.findIndex((c) => RL_HDR.test(c)),
      };
      start = i + 1;
      break;
    }
  }

  const out: EvidenceMeasurement[] = [];
  for (const r of rows.slice(start)) {
    let name = "";
    let cas = "";
    let resultCell = "";
    let unit = "";
    let qual = "";
    let rlCell = "";
    if (header) {
      name = r[header.name] ?? "";
      resultCell = r[header.result] ?? "";
      cas = header.cas >= 0 ? r[header.cas] ?? "" : "";
      unit = header.unit >= 0 ? r[header.unit] ?? "" : "";
      qual = header.qual >= 0 ? r[header.qual] ?? "" : "";
      rlCell = header.rl >= 0 ? r[header.rl] ?? "" : "";
    } else {
      name = r[0] ?? "";
      const rest = r.slice(1);
      cas = rest.find((c) => CAS_RE.test(c)) ?? "";
      const resIdx = rest.findIndex((c) => !CAS_RE.test(c) && (NUM_RE.test(c) || /^(nd|non-?detect)$/i.test(c)));
      if (resIdx < 0) continue;
      resultCell = rest[resIdx];
      const next = rest[resIdx + 1] ?? "";
      if (UNIT_RE.test(next)) unit = next;
      else if (/^[a-z]$/i.test(next)) qual = next;
    }
    if (!CAS_RE.test(cas)) cas = "";
    name = name.replace(/^"|"$/g, "").trim();
    if (!name || NAME_HDR.test(name) && RESULT_HDR.test(resultCell)) continue;

    const ndWord = /^(nd|non-?detect|bdl|<\s*rl)$/i.test(resultCell.trim());
    const parsed = parseNumber(resultCell);
    const rl = parseNumber(rlCell).value;
    const qualifierND = /\bU\b/.test(qual) || /\s+U$/.test(resultCell.trim());
    const nonDetect = ndWord || parsed.lessThan || qualifierND;
    const raw = parsed.value ?? (nonDetect ? rl : null);
    if (raw === null) continue;

    const norm = normalizeUnit(raw, unit);
    out.push({
      name,
      cas,
      value: norm.value,
      unit: norm.unit,
      non_detect: nonDetect,
      source: "file_parsed",
      file_id: file.id,
      file_name: file.name,
    });
  }
  return out;
}

const keyNorm = (s: string) => s.toLowerCase().replace(/[\s\-_(),'.]/g, "");

/**
 * Collapses repeated parameters to the highest detected value (conservative).
 * `keyOf` lets callers resolve aliases to a standard; defaults to CAS or name.
 */
export function mergeMeasurements(
  list: EvidenceMeasurement[],
  keyOf: (m: EvidenceMeasurement) => string = (m) => m.cas || keyNorm(m.name),
): { merged: EvidenceMeasurement[]; duplicates: Record<string, EvidenceMeasurement[]> } {
  const groups = new Map<string, EvidenceMeasurement[]>();
  for (const m of list) {
    const k = keyOf(m);
    const g = groups.get(k);
    if (g) g.push(m);
    else groups.set(k, [m]);
  }
  const merged: EvidenceMeasurement[] = [];
  const duplicates: Record<string, EvidenceMeasurement[]> = {};
  for (const [k, g] of groups) {
    const detected = g.filter((m) => !m.non_detect);
    const pool = detected.length > 0 ? detected : g;
    merged.push(pool.reduce((a, b) => (b.value > a.value ? b : a)));
    if (g.length > 1) duplicates[k] = g;
  }
  return { merged, duplicates };
}

/** Reads `analysis.evidence_measurements`, dropping malformed rows. */
export function evidenceMeasurementsFromAnalysis(analysis: unknown): EvidenceMeasurement[] {
  const arr = (analysis as { evidence_measurements?: unknown } | null)?.evidence_measurements;
  if (!Array.isArray(arr)) return [];
  return arr.filter(
    (m): m is EvidenceMeasurement =>
      !!m &&
      typeof (m as EvidenceMeasurement).name === "string" &&
      typeof (m as EvidenceMeasurement).value === "number" &&
      Number.isFinite((m as EvidenceMeasurement).value) &&
      ((m as EvidenceMeasurement).source === "file_parsed" ||
        (m as EvidenceMeasurement).source === "ai_extracted_pdf"),
  );
}
